import type { AddQuizAttemptQuestionInput } from "@brookbush/types";

import clsx from "clsx";
import { motion } from "framer-motion";
import { CheckCircle2Icon, XCircleIcon, XIcon } from "lucide-react";
import posthog from "posthog-js";
import { useMemo, useState } from "react";

import fetcher from "~/utils/fetcher.client";

export interface QuizQuestion {
	id: number;
	stem: string;
	answer: string;
	distractors: string[];
	rationale: string;
}

export interface QuizData {
	quizId: number;
	questions: QuizQuestion[];
}

export interface QuizResults {
	score: number;
	incorrectQuestions: {
		stem: string;
		selected: string;
		correct: string;
	}[];
}

interface ChatQuizProps {
	quiz: QuizData;
	onClose: (results?: QuizResults) => void;
}

const ChatQuiz = ({ quiz, onClose }: ChatQuizProps) => {
	const [answers, setAnswers] = useState<AddQuizAttemptQuestionInput[]>([]);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [score, setScore] = useState(0);
	const [isFinished, setIsFinished] = useState(false);

	const question = quiz.questions[currentIndex];

	// Stable random shuffle using useMemo to avoid reshuffling on render
	const options = useMemo(() => {
		const rawOptions = [
			question?.answer,
			...(question?.distractors ?? []),
		].filter(Boolean);
		return rawOptions.sort(() => Math.random() - 0.5);
	}, [question]);

	const handleSelect = (option: string) => {
		if (isSubmitted) return;
		setSelectedAnswer(option);
	};

	const handleSubmit = () => {
		if (!selectedAnswer) return;
		if (selectedAnswer === question.answer) {
			setScore((s) => s + 1);
		}

		setAnswers((prev) => [
			...prev,
			{
				quizQuestionId: question.id,
				selectedOption: selectedAnswer,
				isCorrect: selectedAnswer === question.answer,
			},
		]);

		setIsSubmitted(true);
	};

	const handleNext = () => {
		if (currentIndex < quiz.questions.length - 1) {
			setCurrentIndex((c) => c + 1);
			setSelectedAnswer(null);
			setIsSubmitted(false);
		} else {
			const saveAttempt = async () => {
				try {
					await fetcher("/quiz/attempt", {
						api: "chat",
						method: "POST",
						data: {
							quizId: quiz.quizId,
							questions: answers,
						},
					});
				} catch (err) {
					console.error("Failed to save quiz attempt", err);
				}
			};

			saveAttempt();
			setIsFinished(true);
		}
	};

	const handleFinish = () => {
		posthog.capture("quiz:attempt_completed", {
			quizId: quiz.quizId,
			score,
			totalQuestions: quiz.questions.length,
		});

		const incorrectQuestions = answers
			.filter((a) => !a.isCorrect)
			.map((a) => {
				const question = quiz.questions.find((q) => q.id === a.quizQuestionId);
				return {
					stem: question?.stem ?? "Unknown",
					selected: a.selectedOption,
					correct: question?.answer ?? "Unknown",
				};
			});

		onClose({
			score,
			incorrectQuestions,
		});
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			exit={{ opacity: 0, y: 20 }}
			className="absolute inset-0 z-50 flex flex-col bg-white md:rounded-2xl dark:bg-slate-900"
		>
			<header className="flex items-center justify-between border-slate-200 border-b px-4 py-3 dark:border-slate-800">
				<h2 className="font-semibold text-slate-900 dark:text-white">
					Practice Quiz
				</h2>
				<button
					type="button"
					onClick={() => {
						posthog.capture("quiz:attempt_cancelled", {
							quizId: quiz.quizId,
							questionsAnswered: answers.length,
						});
						onClose();
					}}
					className="rounded-full p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
				>
					<XIcon className="size-5" />
				</button>
			</header>

			<main className="flex flex-1 flex-col overflow-y-auto p-4 md:p-6">
				{isFinished ? (
					<div className="flex flex-1 flex-col items-center justify-center text-center">
						<div className="mb-4 text-6xl">🎉</div>
						<h3 className="mb-2 font-bold text-2xl text-slate-900 dark:text-white">
							Quiz Complete!
						</h3>
						<p className="text-slate-600 dark:text-slate-400">
							You scored {score} out of {quiz.questions.length}.
						</p>
						<button
							type="button"
							onClick={handleFinish}
							className="mt-8 rounded-full bg-sky-600 px-8 py-3 font-medium text-white hover:bg-sky-700"
						>
							Return to Chat
						</button>
					</div>
				) : (
					<div className="mx-auto flex w-full max-w-xl flex-col">
						<div className="mb-6 flex items-center justify-between text-slate-500 text-sm">
							<span>
								Question {currentIndex + 1} of {quiz.questions.length}
							</span>
							<span>
								Score: {score}/{quiz.questions.length}
							</span>
						</div>

						<h3 className="mb-8 font-medium text-lg text-slate-900 md:text-xl dark:text-white">
							{question?.stem}
						</h3>

						<div className="flex flex-col gap-3">
							{options.map((option) => {
								const isSelected = selectedAnswer === option;
								const isCorrect = option === question?.answer;

								let stateClass =
									"border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600";

								if (isSelected && !isSubmitted) {
									stateClass = "border-sky-500 bg-sky-50 dark:bg-sky-900/20";
								} else if (isSubmitted) {
									if (isCorrect) {
										stateClass =
											"border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20";
									} else if (isSelected) {
										stateClass =
											"border-rose-500 bg-rose-50 dark:bg-rose-900/20";
									} else {
										stateClass =
											"border-slate-200 bg-slate-50 opacity-50 dark:border-slate-800 dark:bg-slate-900";
									}
								}

								return (
									<button
										key={option}
										type="button"
										disabled={isSubmitted}
										onClick={() => handleSelect(option)}
										className={clsx(
											"flex w-full items-center justify-between gap-4 rounded-xl border p-4 text-left font-medium transition-colors",
											stateClass,
										)}
									>
										<span className="text-slate-900 dark:text-white">
											{option}
										</span>
										{isSubmitted && isCorrect && (
											<CheckCircle2Icon className="size-5 shrink-0 text-emerald-500" />
										)}
										{isSubmitted && isSelected && !isCorrect && (
											<XCircleIcon className="size-5 shrink-0 text-rose-500" />
										)}
									</button>
								);
							})}
						</div>

						{isSubmitted && (
							<motion.div
								initial={{ opacity: 0, height: 0 }}
								animate={{ opacity: 1, height: "auto" }}
								className="mt-6 rounded-xl bg-slate-100 p-4 text-slate-700 text-sm dark:bg-slate-800 dark:text-slate-300"
							>
								<strong>Rationale:</strong> {question?.rationale}
							</motion.div>
						)}

						<div className="mt-8">
							{!isSubmitted ? (
								<button
									type="button"
									disabled={!selectedAnswer}
									onClick={handleSubmit}
									className="w-full rounded-xl bg-slate-900 py-4 font-medium text-white disabled:opacity-50 dark:bg-white dark:text-slate-900"
								>
									Submit Answer
								</button>
							) : (
								<button
									type="button"
									onClick={handleNext}
									className="w-full rounded-xl bg-sky-600 py-4 font-medium text-white hover:bg-sky-700"
								>
									{currentIndex < quiz.questions.length - 1
										? "Next Question"
										: "Finish Quiz"}
								</button>
							)}
						</div>
					</div>
				)}
			</main>
		</motion.div>
	);
};

export default ChatQuiz;
